import { useMemo, useState } from "react";
import "./accounting.css";
import { useNotify } from "../../components/notifications";
import { useDeleteFlow } from "../../components/confirm";
import { FormTitle } from "../../components/FormTitle";
import { DataTable, type DTColumn } from "./DataTable";
import { useFetchMs } from "../../hooks/useFetchMs";
import { useNavActions } from "../../hooks/useNavActions";
import { SettingsDetailPage, type DetailDataAdapter, type DetailConfig } from "../modules/SettingsDetailPage";
import type { CrudField } from "../modules/moduleCrud";
import { usePaymentTerms, useSavePaymentTerm, useDeletePaymentTerm, errMessage, type PaymentTerm } from "./accounting";

interface Props {
  onHome?: () => void;
}

const COLUMNS: DTColumn<PaymentTerm>[] = [{ key: "terms", label: "Terms", value: (r) => `${r.terms} days` }];

/** Payment Term detail fields — drives the shared <SettingsDetailPage>. */
const PAYMENT_TERMS_FIELDS: CrudField[] = [
  {
    key: "terms",
    label: "Terms (days)",
    type: "number",
    required: true,
    min: 1,
    step: 1,
    requiredMessage: "Enter a whole number of days greater than 0.",
  },
];
const PAYMENT_TERMS_CONFIG: DetailConfig = {
  fields: PAYMENT_TERMS_FIELDS,
  formName: "Payment Terms",
  entityLabel: "Payment Term",
  idField: "id",
  // Detail-view field layout standard (standing rule 12) — label BESIDE its field
  // in a fixed-width left-aligned column, 26px control. ONE column: a single field
  // cannot fill a 50/50 split (mirrors Region, 2026-09-26 — converted from the old
  // popup to the full-page detail per the no-popup standard).
  singleColumn: true,
  sideLabels: true,
};

/** Settings ▸ Utility ▸ Accounting ▸ Payment Terms — editable master list.
 *  New / Edit opens the shared full-page <SettingsDetailPage> (BO UI Standard §4,
 *  standing rule 12) via a data adapter over the existing Payment Term mutations —
 *  no bespoke popup (converted 2026-09-26, matching Region). */
export function PaymentTermsPage({ onHome }: Props) {
  const notify = useNotify();
  // Module Access Control: hide (never grey) the actions the role does not hold.
  const perm = useNavActions("acc-paymentterms");
  const runDelete = useDeleteFlow();
  const { data: rows = [], isFetching, refetch } = usePaymentTerms();
  const loadMs = useFetchMs(isFetching);
  const save = useSavePaymentTerm();
  const del = useDeletePaymentTerm();

  // undefined = list view; null = New detail; a PaymentTerm = Edit detail.
  const [editing, setEditing] = useState<PaymentTerm | null | undefined>(undefined);
  const openNew = () => setEditing(null);
  const openEdit = (r: PaymentTerm) => setEditing(r);
  const close = () => setEditing(undefined);

  // Shared ALPACA-style delete flow over the CHECKED rows (toolbar Delete): ONE
  // centred danger confirm → delete each → "Deleted." (owner request 2026-10-13 —
  // Delete moved from the per-row Actions column to the toolbar on selection).
  const removeSelected = async (sel: PaymentTerm[]) => {
    if (sel.length === 0) return;
    const one = sel.length === 1 ? sel[0] : undefined;
    await runDelete({
      formName: "Payment Terms",
      message: one ? `Delete payment term "${one.terms} days"?` : `Delete ${sel.length} payment terms?`,
      delete: async () => {
        for (const r of sel) await del.mutateAsync(r.id);
      },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  const siblingIds = useMemo(() => rows.map((r) => r.id), [rows]);

  // Adapter: plug the existing Payment Term mutations + list into the shared
  // detail view. Memoised on `rows` so the detail's load effect stays stable.
  const adapter = useMemo<DetailDataAdapter>(
    () => ({
      load: async (rid) => {
        const r = rows.find((x) => x.id === rid);
        return { terms: r?.terms ?? "" };
      },
      create: async (payload) => {
        await save.mutateAsync({ terms: Number(payload.terms) });
        return null; // server id not surfaced by the mutation; Save→list
      },
      update: async (rid, payload) => {
        await save.mutateAsync({ id: rid, terms: Number(payload.terms) });
      },
      // save.mutateAsync is stable across renders; rows is the only real dep.
    }),
    [rows],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  if (editing !== undefined) {
    return (
      <SettingsDetailPage
        config={PAYMENT_TERMS_CONFIG}
        adapter={adapter}
        id={editing ? editing.id : null}
        title="Payment Terms"
        group="Accounting"
        siblingIds={siblingIds}
        onHome={onHome ?? (() => {})}
        onNavigate={(nid) => {
          const r = rows.find((x) => x.id === nid);
          if (r) openEdit(r);
        }}
        onClose={close}
        // Utility ▸ Accounting form rendering SettingsDetailPage directly (not via
        // ModuleListView), so it is de-yellowed + square-cornered here like every
        // other Utility detail (owner request 2026-10-13).
        forceSquareControls
        onSaved={() => {
          /* useSavePaymentTerm invalidates the list query automatically */
        }}
      />
    );
  }

  return (
    <div className="bl-page mlv-host">
      <div className="bl-top">
        <div className="dp-breadcrumb" style={{ paddingTop: 12 }}>
          <a onClick={onHome}>Home</a>
          <i className="ti ti-chevron-right" />
          <span>Settings</span>
          <i className="ti ti-chevron-right" />
          <span>Utility</span>
          <i className="ti ti-chevron-right" />
          <span>Accounting</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">Payment Terms</span>
        </div>
        {/* BO UI Standard v2: shared FormTitle heading (nav icon tile + title +
            the SINGLE top load-time chip) ON the grey page, then "N items". */}
        <div className="dp-page-hdr mlv-hdr--empty">
          <div className="dp-title-row">
            <FormTitle pageId="acc-paymentterms" auditFormName="Payment Terms" loadMs={loadMs} loading={isFetching} />
          </div>
        </div>
      </div>

      {/* Toolbar row · search row · ONE white grid card (composed by DataTable). */}
      <DataTable
        exportName="PaymentTerms"
        columns={COLUMNS}
        rows={rows}
        getId={(r) => r.id}
        loading={isFetching}
        emptyText="No payment terms yet — click New to add one."
        onNew={perm.create ? openNew : undefined}
        onEdit={perm.write ? openEdit : undefined}
        onDeleteSelected={perm.delete ? removeSelected : undefined}
        onRefresh={() => void refetch()}
      />
    </div>
  );
}
